import * as THREE from 'three';
import { KOKUN_CITY, PhysicsWorld, platformCenter, type MapDef } from '@kokun/shared';
import { Engine } from '../core/engine';
import { Input } from '../core/input';
import { ThirdPersonCamera } from '../camera/third-person-camera';
import { buildCity, type CityView } from '../scene/city-builder';
import { Particles } from '../effects/particles';
import { ObjectsView } from './objects-view';
import { connection } from '../../multiplayer/connection';
import { settings } from '../../app/settings';

/**
 * Contenedor de la escena 3D compartida por el menú y la partida: motor, ciudad, objetos,
 * partículas, cámara y entrada. Se crea una sola vez para no reconstruir el mapa.
 */
export class World3D {
  readonly engine: Engine;
  readonly input: Input;
  readonly map: MapDef = KOKUN_CITY;
  readonly physics: PhysicsWorld;
  readonly city: CityView;
  readonly objects: ObjectsView;
  readonly particles: Particles;
  readonly camera: ThirdPersonCamera;
  readonly dynamic = new THREE.Group();
  vanished = new Set<string>();
  private blackout = 0;
  private alarm = false;
  private flashlight = new THREE.PointLight(0xfff1c1, 0, 16, 1.5);
  serverNow: () => number = () => connection.serverNow();

  constructor(container: HTMLElement) {
    this.engine = new Engine(container);
    this.input = new Input(this.engine.renderer.domElement);
    this.physics = new PhysicsWorld(this.map);
    this.city = buildCity(this.map);
    this.objects = new ObjectsView(this.map, () => this.serverNow());
    this.objects.groundAt = (x, z) => this.physics.groundHeightAt(x, 5, z);
    this.particles = new Particles();
    this.camera = new ThirdPersonCamera(this.engine.camera, this.physics);
    this.engine.scene.add(this.city.root, this.objects.root, this.particles.mesh, this.dynamic, this.flashlight);
    this.engine.onFrame((dt, t) => this.update(dt, t));
    this.engine.start();
  }

  setBlackout(on: boolean) {
    this.blackout = on ? 1 : 0;
  }
  setAlarm(on: boolean) {
    this.alarm = on;
  }

  setVanished(ids: string[]) {
    this.vanished = new Set(ids);
    this.physics.setHidden(ids);
  }

  attachFlashlight(pos: THREE.Vector3) {
    this.flashlight.position.set(pos.x, pos.y + 2.5, pos.z);
  }

  private envK = 0;
  private update(dt: number, t: number) {
    const st = this.serverNow() / 1000;
    for (const p of this.map.movingPlatforms) {
      const m = this.city.movers.get(p.id)!;
      m.position.set(...platformCenter(p, st));
    }
    for (const [id, m] of this.city.vanish) {
      const mat = m.material as THREE.MeshToonMaterial;
      const target = this.vanished.has(id) ? 0 : 0.95;
      mat.opacity += (target - mat.opacity) * Math.min(1, dt * 8);
      m.visible = mat.opacity > 0.05;
    }
    this.city.update(dt, t);
    this.objects.update(dt, t);
    this.particles.update(dt);

    // Iluminación de eventos (apagón y alarma)
    this.envK += (this.blackout - this.envK) * Math.min(1, dt * 2);
    const k = this.envK;
    const e = this.engine;
    e.hemi.intensity = 1.1 * (1 - k * 0.85);
    e.sun.intensity = 2.2 * (1 - k * 0.92);
    const bg = new THREE.Color(0x8fd3ff).lerp(new THREE.Color(0x0b0f2b), k);
    if (this.alarm && !settings.get().reduceEffects) bg.lerp(new THREE.Color(0xff1e3c), (Math.sin(t * 8) * 0.5 + 0.5) * 0.35);
    (e.scene.background as THREE.Color).copy(bg);
    (e.scene.fog as THREE.Fog).color.copy(bg).lerp(new THREE.Color(0xffffff), 0.25 * (1 - k));
    this.flashlight.intensity = k * 30;
    for (const b of this.city.lampBulbs) (b.material as THREE.MeshBasicMaterial).color.setHex(k > 0.3 ? 0xffe066 : 0xfff3b0);
  }
}
